// PdfViewer.jsx
import React, { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import ProPagination from "./ProPagination";
import "./pdfWorker";
import { Row, Spinner } from "reactstrap";

const PAGE_MAX_WIDTH = 1000; // 👈 tweak for centered mode (900–1200 is common)

export default function PdfViewer({ file, fullWidth = false, initialPage = 1, onPageChange }) {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);
  const [docLoading, setDocLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);

  const shellRef = useRef(null);        // measures outer shell
  const [containerWidth, setContainerWidth] = useState(0);

  const isLoading = docLoading || pageLoading;

  useEffect(() => {
    if (totalPages > 0) onPageChange?.({ page, totalPages });
  }, [page, totalPages]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w && w !== containerWidth) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerWidth]);

  useEffect(() => {
    if (!isLoading && shellRef.current) {
      shellRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isLoading]);

  // 👇 Width logic:
  // - fullWidth: use all measured width
  // - centered: cap at PAGE_MAX_WIDTH
  const pageWidth =
    containerWidth === 0
      ? 0
      : fullWidth
        ? containerWidth
        : Math.min(containerWidth, PAGE_MAX_WIDTH);

  return (
    <div ref={shellRef}>
      {error && (
        <div style={{ color: "crimson", marginBottom: 8 }}>{String(error)}</div>
      )}

      <div style={{ position: "relative", minHeight: "60vh" }}>
        <Document
          file={file}
          loading={null}
          onLoadSuccess={(doc) => {
            setError(null);
            setTotalPages(doc.numPages || 0);
            setDocLoading(false);
            setPageLoading(true);
            const pages = doc.numPages || 0;
            const start = Math.min(Math.max(1, Number(initialPage) || 1), pages || 1);
            setPage(start);
          }}
          onLoadError={(err) => {
            setError(String(err?.message || err));
            setDocLoading(false);
          }}
          onSourceError={(err) => setError(String(err?.message || err))}
        >
          {pageWidth > 0 && (
            // 👇 This wrapper centers the page when it's narrower than the container
            <div
              style={{
                display: "flex",
                justifyContent: "center",        // center on desktop
              }}
            >
              <Page
                key={page}
                pageNumber={page}
                width={pageWidth}                 // responsive width
                renderAnnotationLayer
                renderTextLayer
                loading={null}
                onRenderSuccess={() => setPageLoading(false)}
                onRenderError={(err) => {
                  setError(String(err?.message || err));
                  setPageLoading(false);
                }}
              />
            </div>
          )}
        </Document>

        {!!isLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 vh-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 10 }}
          >
            <div className="text-center" style={{ marginTop: -160 }}>
              <Spinner style={{ width: "3rem", height: "3rem" }} />
              <div className="mt-2">
                {docLoading ? "Loading resource…" : "Rendering resource…"}
              </div>
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Row
          className="justify-content-between align-items-center bg-transparent mt-2"
        >
          <ProPagination
            totalPages={totalPages}
            currentPage={page}
            onChange={(p) => !isLoading && setPage(p)}
            disabled={isLoading}
            siblingCount={1}
            boundaryCount={1}
          />
        </Row>
      )}
    </div>
  );
}
